import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge, Button, Card, EmptyState, Input, Spinner } from '../../components/ui';
import { api } from '../../lib/api';

interface MemberRow {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  skillLevel: string;
  membershipStatus: string;
  duesStatus: string;
  duesPaidUntil: string | null;
}

interface MemberList {
  items: MemberRow[];
  total: number;
  page: number;
  pageSize: number;
}

export function MembersPage() {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery<MemberList>({
    queryKey: ['members', q, page],
    queryFn: async () => {
      const { data } = await api.get('/admin/members', { params: { q, page, pageSize: 50 } });
      return data;
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-3xl">Members</h1>
        <Link
          to="/admin/members/new"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-navy-900 text-cream-100 text-sm font-medium hover:bg-navy-800"
        >
          Add member
        </Link>
      </div>

      <Input placeholder="Search name, email, phone…" value={q} onChange={(e) => setQ(e.target.value)} />

      {isLoading ? (
        <Spinner />
      ) : (data?.items ?? []).length === 0 ? (
        <EmptyState title="No members yet" description="Import from CSV or add them one by one." />
      ) : (
        <Card>
          <table className="w-full">
            <thead className="bg-cream-200">
              <tr>
                <th className="text-left px-4 py-2 text-sm font-medium">Name</th>
                <th className="text-left px-4 py-2 text-sm font-medium">Email</th>
                <th className="text-left px-4 py-2 text-sm font-medium">Skill</th>
                <th className="text-left px-4 py-2 text-sm font-medium">Dues</th>
              </tr>
            </thead>
            <tbody>
              {data?.items.map((m) => (
                <tr key={m.id} className="border-t border-cream-300">
                  <td className="px-4 py-2">
                    <Link to={`/admin/members/${m.id}`} className="hover:underline">
                      {m.firstName} {m.lastName}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-sm">{m.email}</td>
                  <td className="px-4 py-2 text-sm">{m.skillLevel}</td>
                  <td className="px-4 py-2">
                    <Badge variant={m.duesStatus === 'PAID' ? 'success' : 'warning'}>{m.duesStatus}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {data && data.total > data.pageSize && (
        <div className="flex justify-between items-center text-sm">
          <Button variant="ghost" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </Button>
          <span>
            Page {data.page} of {Math.ceil(data.total / data.pageSize)}
          </span>
          <Button variant="ghost" disabled={page * data.pageSize >= data.total} onClick={() => setPage((p) => p + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
